import React, { useCallback, useEffect, useMemo, useState } from 'react';

/* eslint-disable react/no-multi-comp */
import cx from 'classnames';
import { v4 as uuid } from 'uuid';

import { Play } from 'education-icons';
import { FormattedMessage } from 'react-intl';
import { useAsyncFn } from 'react-use';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { putRunCode } from '@yandex-int/k-common/client/api/defs/put-run-code';
import { IconPlay } from '@edu-frontend/modules/src/components/ui/Icon';
import { CodeHistory } from '@yandex-int/k-common';
import { Button } from '../button/button';
import { KEYBOARD_TYPES } from '../constants';
import UseKeyboard from '../keyboard/use-keyboard';
import '../robot/font';
import { monospace } from '../robot/font';
import SplitView from '../split-view/split-view';
import { useMobileCodeEditorAvailability } from '../../utils/hooks';
import { HRDSButton } from '../button/hrdsButton';
import { getMe } from '../user/selectors';
import { CodingProblemProps } from './types';
import { useCodingStore } from './state';
import { CodingPropsProvider, useCodingPropsSelector } from './context';

import { useDispatchNetworkError } from './hooks/useDispatchNetworkError';
import { MobileCodeEditor } from './__mobile-code-editor/coding-problem__mobile-code-editor';
import { CodingEditor } from './__editor/coding-problem__editor';
import { CodingInfo } from './__info/coding-problem__info';
import { CodingTests } from './__tests/coding-problem__tests';
import { restoreCodeSession } from './__code-editor/_lib/solve-session';

// @ts-ignore
import styles from './coding-problem.module.scss';

function CodingContent(props: { shouldShowNewMobileEditor: boolean }) {
    const { shouldShowNewMobileEditor } = props;

    const readOnly = useCodingPropsSelector((p) => p.readOnly === true);
    const markup = useCodingPropsSelector((p) => p.problem.markup);
    const problemId = useCodingPropsSelector((p) => p.problem.id);
    const onRunCode = useCodingPropsSelector((p) => p.onRunCode);
    const showCorrectAnswers = useCodingPropsSelector((p) => p.showCorrectAnswers);
    const solveSession = useCodingPropsSelector((p) => p.solveSession);
    const attempt = useCodingPropsSelector((p) => p.attempt);

    const me = useSelector(getMe);

    const params = useParams();

    const lang = markup.languages[0];
    const image = (lang.image || 'PYTHON') as 'PYTHON_BLOCKLY_MIPT_ML' | 'PYTHON';

    const activeCodingTab = useCodingStore((state) => state.activeTab);
    const setActiveCodingTab = useCodingStore((state) => state.setActiveTab);
    const code = useCodingStore((state) => state.code);
    const setCode = useCodingStore((state) => state.setCode);
    const setHelp = useCodingStore((state) => state.setHelp);
    const lastSubmit = useCodingStore((state) => state.lastSubmit[problemId]);
    const setLastSubmit = useCodingStore((state) => state.setLastSubmit);
    const customTestInput = useCodingStore((state) => state.customTestInput);

    const sessionKey = useMemo(() => `coding-${me?.id}-${JSON.stringify(params)}`, [me?.id, params]);

    const [runCodeState, runCodeRequest] = useAsyncFn(async () => {
        if (shouldShowNewMobileEditor) {
            setActiveCodingTab('tests');
        }

        onRunCode?.({ problemId, code, input: customTestInput });

        setLastSubmit(problemId, 'run-code');

        const response = await putRunCode({
            image,
            user_code: code,
            user_input: customTestInput.split(/\r?\n/),
            pre_user_code: lang.pre_user_code,
            post_user_code: lang.post_user_code,
            env: lang.env,
        });

        setHelp(
            problemId,
            response.exception_traceback
                ? {
                      problemId,
                      code,
                      input: customTestInput,
                      error: response.exception_traceback,
                  }
                : null
        );

        const runId = uuid();
        return { runId, ...response };
    }, [code, customTestInput]);

    const runCodeState2: typeof runCodeState = lastSubmit === 'run-code' ? runCodeState : { loading: false };

    useDispatchNetworkError(runCodeState2.error);

    const shouldShowRunCodeButton = !readOnly && shouldShowNewMobileEditor ? activeCodingTab !== 'task' : true;

    const [resizeKey, setResizeKey] = useState(0);

    const initialValue = useCallback((): string | CodeHistory => {
        if (showCorrectAnswers) {
            return lang.author_solution;
        }

        // Restore code history only for a student
        if (!me?.is_teacher && sessionKey) {
            const fromSessionStorage = restoreCodeSession(sessionKey);
            if (fromSessionStorage) {
                return fromSessionStorage.history ?? fromSessionStorage.text;
            }

            if (solveSession) {
                return solveSession.history;
            }
        }

        return attempt?.markers?.user_answer.code || lang.initial_code;
    }, [
        sessionKey,
        lang.author_solution,
        me?.is_teacher,
        showCorrectAnswers,
        solveSession,
        attempt?.markers?.user_answer.code,
        lang.initial_code,
    ]);

    const initialCode = useMemo(() => {
        return initialValue();
    }, [initialValue]);

    useEffect(() => {
        if (typeof initialCode === 'string') {
            setCode(initialCode);
        } else if (typeof initialCode === 'object') {
            setCode(initialCode.lastText);
        }
    }, [initialCode, setCode]);

    return (
        <>
            {shouldShowNewMobileEditor ? (
                <MobileCodeEditor initialValue={initialValue} runCodeState={runCodeState2} sessionKey={sessionKey} />
            ) : (
                <SplitView className={styles.coding} initialSizes={[0.3, 0.7]} minSizes={[280, 360]}>
                    <CodingInfo />
                    <SplitView
                        direction="column"
                        initialSizes={[0.6, 0.4]}
                        minSizes={[300, 260]}
                        onResizeEnd={() => setResizeKey((v) => v + 1)}
                    >
                        <CodingEditor initialValue={initialValue} sessionKey={sessionKey} />
                        <CodingTests resizeKey={resizeKey} runCodeState={runCodeState2} />
                    </SplitView>
                </SplitView>
            )}
            {/* Кнопка "Запустить", которая запускает код. Порталом помещается в Problem Bar */}
            <UseKeyboard keyboardType={KEYBOARD_TYPES.PROBLEM_BAR} show={shouldShowRunCodeButton}>
                {shouldShowNewMobileEditor ? (
                    <HRDSButton
                        disabled={runCodeState.loading}
                        onClick={runCodeRequest}
                        progress={runCodeState.loading}
                        rounded
                        size="l"
                        view="primary"
                        width="max"
                    >
                        <IconPlay className={styles['coding-problem__button-icon']} size="m" />
                        <FormattedMessage id="problemCoding.runCode" />
                    </HRDSButton>
                ) : (
                    <Button
                        accent={Button.ACCENT.NORMAL}
                        icon={<Play />}
                        onClick={runCodeRequest}
                        progress={runCodeState.loading}
                        rounded
                        size={Button.SIZE.L}
                        view={Button.VIEW.PSEUDO}
                    >
                        <FormattedMessage id="problemCoding.runCode" />
                    </Button>
                )}
            </UseKeyboard>
        </>
    );
}

const CodingContentMemo = React.memo(CodingContent);

function Coding(props: CodingProblemProps) {
    const { problem } = props;

    const mobileCodeEditorIsAvailable = useMobileCodeEditorAvailability(problem);

    return (
        <CodingPropsProvider value={props}>
            <CodingContentMemo shouldShowNewMobileEditor={mobileCodeEditorIsAvailable} />
        </CodingPropsProvider>
    );
}

export const CodingProblem = (props: CodingProblemProps) => {
    if (props.previewMode) {
        return <div className={cx(styles.preview, monospace)} />;
    }
    return <Coding {...props} />;
};
